/**
 * Language URL param resolution. Used at app.jsx to resolve ?lang= to a config entry.
 * Priority: (1) match by langFile, (2) match by code. Array order in config defines priority when multiple entries share the same langFile.
 */

export interface LanguageConfigEntry {
  langFile: string;
  code?: string[];
  countries: string[];
  [key: string]: unknown;
}

/** A match from looking up a lang code against the config: the config entry plus the locale key to use. */
export interface LangConfigMatch {
  config: LanguageConfigEntry;
  localeKey: string;
}

/** Shape of the user's language choice when persisted (e.g. localStorage). */
export interface LangPreference {
  country: string;
  code: string;
  langFile: string;
}

/** Map of locale key (e.g. "fr") to translation bundle. */
export type LocaleBundles = Record<string, unknown>;

export function getLangFile(code: string | null | undefined): string {
  if (!code || code === 'undefined') return 'en';
  return code.toLowerCase();
}

export function normalizeLangParam(
  normalizedCode: string,
  languageArray: LanguageConfigEntry[],
  localeBundles: LocaleBundles,
): LangConfigMatch | null {
  const byLangFile = languageArray.find((entry) => {
    return entry.langFile === normalizedCode && localeBundles[entry.langFile];
  });
  if (byLangFile) return { config: byLangFile, localeKey: byLangFile.langFile };

  const byCode = languageArray.find((entry) => {
    return (
      entry.code?.some((c) => {
        return c.toLowerCase() === normalizedCode;
      }) && localeBundles[entry.langFile]
    );
  });
  if (byCode) return { config: byCode, localeKey: byCode.langFile };

  return null;
}

export function toLangPreference(match: LangConfigMatch): LangPreference {
  const { config, localeKey } = match;
  const [country] = config.countries;
  if (country === undefined) {
    throw new Error(`Language config for "${config.langFile}" has no countries defined`);
  }
  const code = config.code?.[0] ?? localeKey.toUpperCase();
  return {
    country,
    code: code.toUpperCase(),
    langFile: localeKey,
  };
}
